import {createComment, createContact, updateContact} from '@seed-hypermedia/client'
import type {HMComment, HMSigner, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {queryKeys} from '@shm/shared'
import {invalidateQueries, getQueryClient} from '@shm/shared/models/query-client'
import type {NavRoute} from '@shm/shared/routes'
import {applyOptimisticComment, buildOptimisticComment, getCommentSubmissionRoute} from '@shm/shared/optimistic-comment'
import {hmId, routeToUrl} from '@shm/shared/utils/entity-id-url'
import {toast} from '@shm/ui/toast'
import {getCurrentAccountUidWithDelegation, getCurrentSigner} from './auth'
import {clearPendingIntent, getPendingIntent, getStoredLocalKeys} from './local-db'
import {webUniversalClient} from './universal-client'

export type SiteMembershipStatus = 'not-member' | 'already-joined' | 'own-site'
export type JoinSiteResult = SiteMembershipStatus | 'joined'
export type PendingIntentResult =
  | {type: 'none'}
  | {type: 'join'; joinStatus: JoinSiteResult}
  | {type: 'follow'}
  | {type: 'comment'; commentUrl: string; comment: HMComment}
  | {type: 'publish-draft'; spaceUrl: string}
  | {type: 'publish-draft-failed'; retryUrl: string}
  | {type: 'publish-draft-existing-space'; spaceUrl: string; draftId: string}

let pendingIntentProcessingPromise: Promise<PendingIntentResult> | null = null

/** Whether the account is a member of the site, owns it, or neither. */
export async function getSiteMembershipStatus(siteUid: string, accountUid?: string): Promise<SiteMembershipStatus> {
  accountUid ??= (await getCurrentAccountUidWithDelegation()) ?? undefined
  if (!accountUid) {
    throw new Error('No account UID available to check site membership')
  }
  if (accountUid === siteUid) {
    return 'own-site'
  }

  const contacts = await webUniversalClient.request('AccountContacts', accountUid)
  const existingContact = contacts.find((c) => c.subject === siteUid)
  return existingContact?.subscribe?.site ? 'already-joined' : 'not-member'
}

async function joinSite(signer: HMSigner, siteUid: string): Promise<JoinSiteResult> {
  console.log('[joinSite] Joining site', {siteUid})
  const membershipStatus = await getSiteMembershipStatus(siteUid)
  if (membershipStatus !== 'not-member') {
    console.log('[joinSite] Site already joined or owned', {siteUid, membershipStatus})
    return membershipStatus
  }

  const accountUid = await getCurrentAccountUidWithDelegation()
  if (!accountUid) {
    throw new Error('No account UID available to join site')
  }
  const contacts = await webUniversalClient.request('AccountContacts', accountUid)
  console.log('[joinSite] Existing Contacts', contacts)
  const existingContact = contacts.find((c) => c.subject === siteUid)
  if (existingContact) {
    console.log('[joinSite] Updating existing contact to add site subscription', {existingContact})
    const contactPayload = await updateContact(
      {
        contactId: existingContact.id,
        subjectUid: siteUid,
        accountUid,
        name: existingContact.name,
        subscribe: {...existingContact.subscribe, site: true},
      },
      signer,
    )
    await webUniversalClient.publish(contactPayload)
  } else {
    console.log('[joinSite] Creating contact for site', {siteUid, accountUid})
    const contactPayload = await createContact(
      {
        subjectUid: siteUid,
        accountUid,
        subscribe: {site: true},
      },
      signer,
    )
    await webUniversalClient.publish(contactPayload)
  }

  invalidateQueries([queryKeys.CONTACTS_ACCOUNT, accountUid])
  invalidateQueries([queryKeys.CONTACTS_SUBJECT, siteUid])
  return 'joined'
}

async function followProfile(signer: HMSigner, profileUid: string) {
  console.log('[followProfile] Following profile', {profileUid})
  const accountUid = await getCurrentAccountUidWithDelegation()
  if (!accountUid) {
    throw new Error('No account UID available to follow profile')
  }
  const contacts = await webUniversalClient.request('AccountContacts', accountUid)
  const existingContact = contacts.find((c) => c.subject === profileUid)

  if (existingContact && (existingContact.subscribe?.profile || !existingContact.subscribe)) {
    console.log('[followProfile] Already following profile', {existingContact})
    return
  }

  if (existingContact) {
    console.log('[followProfile] Updating existing contact to add profile subscription', {existingContact})
    const contactPayload = await updateContact(
      {
        contactId: existingContact.id,
        subjectUid: profileUid,
        accountUid,
        name: existingContact.name,
        subscribe: {...existingContact.subscribe, profile: true},
      },
      signer,
    )
    await webUniversalClient.publish(contactPayload)
  } else {
    const contactPayload = await createContact(
      {
        subjectUid: profileUid,
        accountUid,
        subscribe: {profile: true},
      },
      signer,
    )
    await webUniversalClient.publish(contactPayload)
  }

  invalidateQueries([queryKeys.CONTACTS_ACCOUNT, accountUid])
  invalidateQueries([queryKeys.CONTACTS_SUBJECT, profileUid])
}

/**
 * Process any pending intent saved before auth redirect.
 * Returns the processed intent result, including any comment navigation URL.
 */
export async function processPendingIntent(originHomeId?: UnpackedHypermediaId): Promise<PendingIntentResult> {
  if (pendingIntentProcessingPromise) {
    console.log('[processPendingIntent] Reusing in-flight pending intent processing')
    return pendingIntentProcessingPromise
  }

  pendingIntentProcessingPromise = runProcessPendingIntent(originHomeId).finally(() => {
    pendingIntentProcessingPromise = null
  })

  return pendingIntentProcessingPromise
}

async function runProcessPendingIntent(originHomeId?: UnpackedHypermediaId): Promise<PendingIntentResult> {
  console.log('[processPendingIntent] START. originHomeId:', originHomeId)
  const intent = await getPendingIntent()
  console.log('[processPendingIntent] intent:', intent?.type ?? 'none')
  if (!intent) return {type: 'none'}

  const signer = await getCurrentSigner()
  if (!signer) {
    console.error('No signer available to process pending intent')
    await clearPendingIntent()
    return {type: 'none'}
  }

  if (intent.type === 'join') {
    console.log('[processPendingIntent] Join intent', intent)
    const joinStatus = await joinSite(signer, intent.subjectUid)
    await clearPendingIntent()
    return {type: 'join', joinStatus}
  }

  if (intent.type === 'follow') {
    console.log('[processPendingIntent] Follow intent', intent)
    await followProfile(signer, intent.profileUid)
    await clearPendingIntent()
    return {type: 'follow'}
  }

  if (intent.type === 'publish-draft') {
    console.log('[processPendingIntent] Publish-draft intent', intent)
    const accountUid = await getCurrentAccountUidWithDelegation()
    if (!accountUid) {
      await clearPendingIntent()
      return {type: 'none'}
    }
    try {
      const existingHome = (await webUniversalClient.request('Resource', hmId(accountUid, {path: []}))) as {
        type?: string
        document?: {metadata?: {siteUrl?: string | null}}
      }
      if (existingHome?.type === 'document') {
        await clearPendingIntent()
        const siteUrl = existingHome.document?.metadata?.siteUrl ?? null
        return {
          type: 'publish-draft-existing-space',
          spaceUrl: siteUrl || `/hm/${accountUid}`,
          draftId: intent.draftId,
        }
      }
    } catch (e) {
      console.warn('[processPendingIntent] existing-home check failed, proceeding to publish', e)
    }
    const {adoptPendingSpaceDraft, discardSpaceHomeDraft, repointSpaceHomeDraftToAccount} = await import(
      './document-edit/web-create-space-draft'
    )
    try {
      const {publishWebDocument} = await import('./document-edit/web-document-actors')
      // Re-key the anonymous home draft to the new account, then publish it.
      const homeId = await adoptPendingSpaceDraft(intent.draftId, accountUid)
      if (!homeId) {
        await clearPendingIntent()
        return {type: 'none'}
      }
      await publishWebDocument(
        {
          documentId: homeId,
          draftId: intent.draftId,
          deps: [],
          metadata: {},
          navigation: undefined,
          publishAccountUid: accountUid,
          deletedChildDraftIds: [],
        },
        {
          docId: homeId,
          getEditor: () => null,
          client: webUniversalClient,
          getSigner: (uid) => {
            if (!webUniversalClient.getSigner) throw new Error('No signer available for publish')
            return webUniversalClient.getSigner(uid)
          },
          getCapabilityCid: () => undefined,
          onPublishSuccess: () => {},
        },
      )
      await discardSpaceHomeDraft(intent.draftId)
      await clearPendingIntent()
      return {type: 'publish-draft', spaceUrl: `/hm/${accountUid}`}
    } catch (e) {
      console.error('Failed to process publish-draft intent:', e)
      toast.error('Your account was created, but publishing your space failed. You can try publishing again.')
      await clearPendingIntent()
      // Re-point the home draft to a placeholder edit route
      // under the new account and redirect there for retry.
      const retryUrl = await repointSpaceHomeDraftToAccount(intent.draftId, accountUid)
      if (retryUrl) return {type: 'publish-draft-failed', retryUrl}
      return {type: 'none'}
    }
  }

  if (intent.type === 'comment') {
    console.log('[processPendingIntent] Comment intent', intent)
    const targetSiteUid = intent.docId.uid
    if (targetSiteUid) {
      await joinSite(signer, targetSiteUid)
    }
    console.log('[processPendingIntent] Creating comment')
    try {
      const storedKeys = await getStoredLocalKeys()
      if (!storedKeys) {
        console.warn('No key pair available to process pending comment intent')
        await clearPendingIntent()
        return {type: 'none'}
      }

      const {docId, content} = intent
      const commentPayload = await createComment(
        {
          docId,
          docVersion: intent.docVersion,
          content,
          replyCommentVersion: intent.replyCommentVersion,
          rootReplyCommentVersion: intent.rootReplyCommentVersion,
          quoting: intent.quotingBlockId ? {blockId: intent.quotingBlockId, range: intent.quotingRange} : undefined,
        },
        signer,
      )

      const accountUid = await getCurrentAccountUidWithDelegation()
      if (!accountUid) throw new Error('No account available to publish comment')
      const comment = await buildOptimisticComment({
        commentPayload,
        authorUid: accountUid,
        docId,
        docVersion: intent.docVersion,
        contentBlocks: content,
        replyParentId: intent.replyCommentId,
        threadRootVersion: intent.rootReplyCommentVersion,
        quoting: intent.quotingBlockId ? {blockId: intent.quotingBlockId, range: intent.quotingRange} : undefined,
      })
      const response = await webUniversalClient.publish(commentPayload)
      const version = response.cids[0]
      if (!version) throw new Error('Published comment has no version')
      comment.version = version
      applyOptimisticComment(getQueryClient(), docId, comment)

      invalidateQueries([queryKeys.QUERY_BLOCK], {refetchType: 'all'})
      invalidateQueries([queryKeys.DOCUMENT_ACTIVITY])
      invalidateQueries([queryKeys.DOCUMENT_DISCUSSION])
      invalidateQueries([queryKeys.DOCUMENT_COMMENTS])
      invalidateQueries([queryKeys.DOCUMENT_INTERACTION_SUMMARY])
      invalidateQueries([queryKeys.DOC_CITATIONS])
      invalidateQueries([queryKeys.BLOCK_DISCUSSIONS])
      invalidateQueries([queryKeys.ACTIVITY_FEED])

      const originalRoute: NavRoute = intent.commentRoute || {
        key: 'comments',
        id: docId,
        openComment: intent.replyCommentId,
      }
      const commentRoute = getCommentSubmissionRoute(originalRoute, comment.id) || originalRoute
      const commentUrl = routeToUrl(commentRoute, {hostname: null, originHomeId})
      if (!commentUrl) {
        throw new Error('Failed to build comment URL')
      }
      clearCommentDraft(docId.id, intent.replyCommentId, intent.quotingBlockId, intent.quotingRange)
      await clearPendingIntent()

      console.log('[processPendingIntent] END. commentRoute:', commentRoute)
      return {type: 'comment', commentUrl, comment}
    } catch (e) {
      console.error('Failed to process pending comment intent:', e)
      return {type: 'none'}
    }
  }

  return {type: 'none'}
}

function clearCommentDraft(
  docId: string,
  replyCommentId?: string | null,
  quotingBlockId?: string,
  quotingRange?: {start: number; end: number},
) {
  const parts = ['comment-draft', docId]
  if (replyCommentId) parts.push(`reply-${replyCommentId}`)
  if (quotingBlockId) parts.push(`quote-${quotingBlockId}`)
  if (quotingRange) parts.push(`range-${quotingRange.start}-${quotingRange.end}`)
  const key = parts.join('-')
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore — localStorage may not be available
  }
}
